import { zodResolver } from "@hookform/resolvers/zod";
import {
  AttachFile,
  Cancel,
  CloudDownload,
  Delete,
  Folder,
} from "@mui/icons-material";
import {
  Alert,
  Autocomplete,
  Avatar,
  Button,
  CircularProgress,
  Container,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import _ from "lodash";
import { useCallback, useRef, useState } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import FormTextField from "../components/Common/FormTextField";
import { DashboardLayout } from "../components/DashboardLayout/DashboardLayout";
import { useConfigs, useCreateAsset } from "../components/Orion/OrionQueries";
import { PageHeader } from "../components/PageHeader/PageHeader";
import { useWorkspaces } from "../components/Rbac/RbacQueries";
import { useSession } from "../components/useSession/useSession";
import { PipelineConfig } from "../interfaces/doc";
import { NewAssetForm, newAssetSchema } from "../schemas/doc_newasset";

const ACCEPTED_TYPES = {
  "application/pdf": [".pdf"],
  "image/*": [".png", ".jpg", ".jpeg"],
};

export function NewAssetPage() {
  const { session } = useSession();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mutate: createAsset } = useCreateAsset();
  const { data: configs } = useConfigs();
  const { data: workspaces } = useWorkspaces();

  const { control, ...methods } = useForm<NewAssetForm>({
    defaultValues: {
      config: undefined,
      config_id: "",
      files: [],
      name: "",
      workspace_id:
        session!.OrionUserGroups.length > 1
          ? null
          : session!.OrionUserGroups[0],
    },
    resolver: zodResolver(newAssetSchema),
  });

  const { config, config_id, files } = methods.watch();

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        setError(
          "Some files were rejected. Please only upload PDFs or images within the size limit."
        );
        return;
      }

      methods.setValue("files", [...files, ...acceptedFiles]);
    },
    [methods, files]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
  });

  const handleConfigUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target?.files?.[0];
    if (file) {
      const fileReader = new FileReader();

      fileReader.onload = (loadEvent) => {
        try {
          methods.setValue(
            "config",
            JSON.parse(loadEvent.target?.result as string) as PipelineConfig
          );
        } catch (error) {
          alert("Error parsing JSON file.");
        }
      };

      fileReader.readAsText(file);
    } else {
      alert("Please upload a valid JSON file.");
    }
  };

  const onSubmit: SubmitHandler<NewAssetForm> = (data) => {
    console.log("Submitted data", data);
    createAsset({ ...data });
    methods.reset();
  };

  const onError: SubmitErrorHandler<NewAssetForm> = (errors) => {
    console.log("Errored data", methods.watch());
    console.log("Errors", errors);
  };

  return (
    <DashboardLayout>
      <Container maxWidth={false} className="overflow-hidden">
        <Stack spacing={2} className="py-2 h-full overflow-hidden">
          <PageHeader
            title="Créer un nouveau dossier"
            breadcrumbs={[
              { title: "Documents", path: "/orion" },
              { title: "Dossiers", path: "/orion/assets" },
              {
                title: "Nouveau dossier",
                path: `/orion/asset/new`,
              },
            ]}
          />
          <form
            onSubmit={methods.handleSubmit(onSubmit, onError)}
            className="flex flex-col gap-4 overflow-hidden"
          >
            <div
              {...getRootProps()}
              className="flex flex-col items-center cursor-pointer text-[#4C4FA2] bg-[#3b2fc32a] border-[#3b2fc32a] border-dashed border-2 rounded-lg p-4"
            >
              <input {...getInputProps()} />
              <CloudDownload sx={{ fontSize: 96 }} />
              {isDragActive ? (
                <p className="font-bold">Drop the files here ...</p>
              ) : (
                <p className="font-bold">
                  Glissez & déposez, ou parcourez vos fichiers
                </p>
              )}
              {error && (
                <Alert color="error" className="mt-3">
                  {error}
                </Alert>
              )}
            </div>
            <List className="overflow-auto">
              {files.map((file, index) => (
                <ListItem
                  key={index}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() =>
                        methods.setValue(
                          "files",
                          files.filter((file, idx) => idx !== index)
                        )
                      }
                    >
                      <Delete />
                    </IconButton>
                  }
                >
                  <ListItemAvatar>
                    <Avatar>
                      <Folder />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText>{file.name}</ListItemText>
                </ListItem>
              ))}
            </List>

            <FormTextField
              control={control}
              label="Nom du dossier"
              name={"name"}
              error={!!methods.formState.errors.name}
              helperText={methods.formState.errors.name?.message}
            />
            <div className="flex justify-between w-full items-center">
              <Controller
                render={({ field: { onChange, ...props }, fieldState }) => (
                  <Autocomplete
                    className="w-2/5"
                    disabled={!!config}
                    options={configs?.map((config) => config._id) || []}
                    getOptionLabel={(config_id) =>
                      configs?.find((config) => config._id === config_id)
                        ?.name || ""
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Configuration"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        fullWidth
                      />
                    )}
                    {...props}
                    onChange={(e, value) => onChange(value)}
                  />
                )}
                name={`config_id`}
                control={control}
              />
              <Typography fontWeight={"bold"}>OU</Typography>
              <div className="flex gap-2 w-2/5 items-center justify-end">
                <input
                  accept="application/json"
                  type="file"
                  onChange={handleConfigUpload}
                  style={{ display: "none" }}
                  ref={fileInputRef}
                />
                <Button
                  variant="contained"
                  className="flex-1"
                  size="small"
                  disabled={!!config_id}
                  startIcon={<AttachFile />}
                  onClick={() => {
                    fileInputRef.current && fileInputRef.current.click();
                  }}
                >
                  Ajouter une configuration
                </Button>

                {config && (
                  <div className="flex items-center gap-2">
                    <Typography>Configuration</Typography>
                    <IconButton
                      size="small"
                      onClick={() => {
                        methods.setValue("config", undefined);
                      }}
                    >
                      <Cancel />
                    </IconButton>
                  </div>
                )}
              </div>
            </div>
            {session!.OrionUserGroups.length > 1 && (
              <Controller
                render={({
                  field: { onChange, value, ...props },
                  fieldState,
                }) => (
                  <Autocomplete
                    options={_.filter(workspaces, (workspace) =>
                      session!.OrionUserGroups.includes(workspace._id)
                    )}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Espace de travail"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                    value={
                      workspaces?.find(
                        (workspace) => workspace._id === value
                      ) ?? null
                    }
                    {...props}
                    onChange={(e, value) =>
                      value ? onChange(value._id) : onChange(value)
                    }
                  />
                )}
                name={`workspace_id`}
                control={control}
              />
            )}

            <Button
              className="w-100"
              type="submit"
              disabled={files.length === 0 || methods.formState.isSubmitting}
              variant="contained"
            >
              {methods.formState.isSubmitting ? (
                <CircularProgress className="text-[var(--mui-palette-primary-main)]" />
              ) : (
                "Créer le dossier"
              )}
            </Button>
          </form>
        </Stack>
      </Container>
    </DashboardLayout>
  );
}
